import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { api } from "../../lib/api";
import { dataHora, num } from "../../lib/format";
import {
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  Package,
  Percent,
  Tags,
  Users,
} from "lucide-react";

const Kpi = ({ label, valor, sub, icone: Icone, tom = "slate", testid }) => {
  const tons = {
    slate: "bg-slate-100 text-slate-700",
    teal: "bg-[#EEF2F9] text-[#1B315E]",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-[#FDECEC] text-[#C1121A]",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <span className={`grid h-8 w-8 place-items-center rounded-lg ${tons[tom]}`}>
          <Icone className="h-4 w-4" />
        </span>
      </div>
      <div className="num mt-1 font-display text-[28px] font-bold leading-tight text-slate-900">
        {valor}
      </div>
      {sub && <div className="text-[11.5px] text-slate-500">{sub}</div>}
    </div>
  );
};

export default function AdminDashboard() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {});
  }, []);

  return (
    <AdminShell
      titulo="Painel administrativo"
      descricao="Visão geral do catálogo, impostos e usuários do sistema."
      acoes={
        <>
          <Link to="/admin/importacao">
            <Button variant="outline" data-testid="admin-atalho-importacao">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Importar planilha
            </Button>
          </Link>
          <Link to="/admin/produtos">
            <Button data-testid="admin-atalho-produtos">
              <Package className="mr-1.5 h-4 w-4" /> Gerenciar produtos
            </Button>
          </Link>
        </>
      }
    >
      {!s ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
              label="Produtos cadastrados"
              valor={num(s.produtos)}
              sub={`${num(s.por_origem["FABRICAÇÃO"] || 0)} fabricação • ${num(
                s.por_origem["IMPORTAÇÃO"] || 0
              )} importação`}
              icone={Package}
              tom="teal"
              testid="admin-kpi-produtos"
            />
            <Kpi
              label="Disponíveis na consulta"
              valor={num(s.produtos_com_preco)}
              sub="produtos com preço cadastrado"
              icone={FileText}
              testid="admin-kpi-com-preco"
            />
            <Kpi
              label="Sem preço (ocultos)"
              valor={num(s.produtos_sem_preco)}
              sub="não aparecem para representantes"
              icone={AlertTriangle}
              tom="amber"
              testid="admin-kpi-sem-preco"
            />
            <Kpi
              label="NCMs com alíquotas"
              valor={num(s.ncms)}
              sub="27 estados × 4 enquadramentos cada"
              icone={Tags}
              testid="admin-kpi-ncms"
            />
            <Kpi
              label="Usuários"
              valor={num(s.usuarios)}
              sub={`${num(s.usuarios_ativos)} ativos`}
              icone={Users}
              testid="admin-kpi-usuarios"
            />
            <Kpi
              label="Orçamentos gerados"
              valor={num(s.orcamentos)}
              icone={FileText}
              testid="admin-kpi-orcamentos"
            />
            <Kpi
              label="Produtos inativos"
              valor={num(s.produtos_inativos)}
              icone={Package}
              testid="admin-kpi-inativos"
            />
            <Kpi
              label="Última importação"
              valor={s.ultima_importacao ? num(s.ultima_importacao.criados + s.ultima_importacao.atualizados) : "—"}
              sub={
                s.ultima_importacao
                  ? `${dataHora(s.ultima_importacao.criado_em)} • ${s.ultima_importacao.arquivo}`
                  : "nenhuma importação manual ainda"
              }
              icone={FileSpreadsheet}
              testid="admin-kpi-import"
            />
          </div>

          {s.produtos_sem_preco > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <div className="text-[13px] text-amber-900">
                  <b>{s.produtos_sem_preco} produtos estão sem preço</b> na planilha importada. Eles
                  ficam visíveis apenas aqui no admin e não aparecem na consulta dos
                  representantes até receberem um valor.
                </div>
              </div>
              <Link to="/admin/produtos?filtro=sem_preco">
                <Button size="sm" variant="outline" data-testid="admin-ver-sem-preco">
                  Preencher preços
                </Button>
              </Link>
            </div>
          )}

          <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="font-display text-sm font-semibold text-slate-900">
                Produtos por NCM
              </div>
              <Link to="/admin/impostos">
                <Button size="sm" variant="outline" data-testid="admin-atalho-impostos">
                  <Percent className="mr-1.5 h-4 w-4" /> Editar alíquotas
                </Button>
              </Link>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[130px]">NCM</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="w-[110px] text-right">Produtos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {s.por_ncm.map((n) => (
                  <TableRow key={n.ncm} data-testid={`admin-ncm-row-${n.ncm}`}>
                    <TableCell className="font-mono-num font-medium">{n.ncm}</TableCell>
                    <TableCell className="max-w-[720px] text-[12.5px] text-slate-600">
                      <span className="line-clamp-2">{n.descricao || "—"}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{n.produtos}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </AdminShell>
  );
}
